import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Lightning, Flame, X, CircleNotch, MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPES = {
  electrical: [
    { value: "service_upgrade_200a", label: "Service Upgrade (200A)" },
    { value: "new_circuit_20a", label: "New Circuit (20A)" },
    { value: "gfci_installation", label: "GFCI Installation" },
    { value: "whole_house_rewire", label: "Whole House Rewire" },
    { value: "ceiling_fan_install", label: "Ceiling Fan Install" },
    { value: "panel_replacement", label: "Panel Replacement" },
  ],
  hvac: [
    { value: "ac_install_3ton", label: "AC Install (3 Ton)" },
    { value: "furnace_replacement", label: "Furnace Replacement" },
    { value: "duct_repair", label: "Duct Repair" },
    { value: "mini_split_install", label: "Mini Split Install" },
    { value: "thermostat_install", label: "Thermostat Install" },
    { value: "duct_cleaning", label: "Duct Cleaning" },
  ],
};

function CreateJobDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    title: "", trade: "electrical", job_type: "", property_type: "residential",
    description: "", address: "", assigned_worker: "", code_jurisdiction: "NEC 2023"
  });
  const [saving, setSaving] = useState(false);

  const jurisdictions = form.trade === "electrical"
    ? ["NEC 2023", "IRC 2021"]
    : ["IMC 2021", "UMC 2021", "ASHRAE 90.1"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.job_type) {
      toast.error("Title and job type are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/jobs`, form);
      toast.success("Job created");
      onCreated();
      onOpenChange(false);
      setForm({ title: "", trade: "electrical", job_type: "", property_type: "residential", description: "", address: "", assigned_worker: "", code_jurisdiction: "NEC 2023" });
    } catch {
      toast.error("Failed to create job");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>New Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            data-testid="job-title-input"
            type="text" placeholder="Job Title"
            value={form.title} onChange={e => setForm({...form, title: e.target.value})}
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.trade} onValueChange={v => setForm({...form, trade: v, job_type: "", code_jurisdiction: v === "electrical" ? "NEC 2023" : "IMC 2021"})}>
              <SelectTrigger data-testid="job-trade-select"><SelectValue placeholder="Trade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v})}>
              <SelectTrigger data-testid="job-property-select"><SelectValue placeholder="Property" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={form.job_type} onValueChange={v => setForm({...form, job_type: v})}>
            <SelectTrigger data-testid="job-type-select"><SelectValue placeholder="Job Type" /></SelectTrigger>
            <SelectContent>
              {(JOB_TYPES[form.trade] || []).map(jt => (
                <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={form.code_jurisdiction} onValueChange={v => setForm({...form, code_jurisdiction: v})}>
            <SelectTrigger data-testid="job-jurisdiction-select"><SelectValue placeholder="Jurisdiction" /></SelectTrigger>
            <SelectContent>
              {jurisdictions.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
            </SelectContent>
          </Select>
          <input
            data-testid="job-address-input"
            type="text" placeholder="Address"
            value={form.address} onChange={e => setForm({...form, address: e.target.value})}
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
          />
          <input
            data-testid="job-worker-input"
            type="text" placeholder="Assigned Worker"
            value={form.assigned_worker} onChange={e => setForm({...form, assigned_worker: e.target.value})}
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
          />
          <textarea
            data-testid="job-description-input"
            placeholder="Description"
            value={form.description} onChange={e => setForm({...form, description: e.target.value})}
            className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-20"
          />
          <button
            data-testid="create-job-submit"
            type="submit" disabled={saving}
            className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
          >
            {saving ? "Creating..." : "Create Job"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterTrade, setFilterTrade] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTrade !== "all") params.trade = filterTrade;
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await axios.get(`${API}/jobs`, { params });
      setJobs(res.data);
    } catch {
      toast.error("Failed to load jobs");
    }
    setLoading(false);
  }, [filterTrade, filterStatus]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const updateStatus = async (jobId, newStatus, progress) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus, progress_percentage: progress });
      toast.success("Job updated");
      fetchJobs();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="jobs-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Jobs Pipeline
        </h1>
        <button
          data-testid="create-job-btn"
          onClick={() => setCreateOpen(true)}
          className="touch-target w-10 h-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center hover:bg-[#001D6C] transition-colors"
        >
          <Plus size={20} weight="bold" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4" data-testid="jobs-filters">
        <Select value={filterTrade} onValueChange={setFilterTrade}>
          <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="filter-trade">
            <FunnelSimple size={12} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trades</SelectItem>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="inspection">Inspection</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <CircleNotch size={28} className="animate-spin text-[#002FA7]" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="wp-card text-center py-8 text-sm text-[#404040]">
          No jobs found. Create your first job.
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job, i) => {
            const isElectrical = job.trade === "electrical";
            return (
              <div
                key={job.id}
                data-testid={`job-item-${job.id}`}
                className={`wp-card ${isElectrical ? "trade-electrical" : "trade-hvac"} animate-slide-up`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{job.title}</h3>
                    <p className="text-xs text-[#404040]">{job.address}</p>
                  </div>
                  <span className="flex items-center gap-1 text-[10px] font-mono-data">
                    {isElectrical ? <Lightning size={10} /> : <Flame size={10} />}
                    {job.trade.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-[#404040] mb-2 line-clamp-2">{job.description}</p>
                <div className="flex items-center gap-3 mb-2">
                  <Progress value={job.progress_percentage} className="flex-1 h-1" />
                  <span className="font-mono-data text-[10px]">{job.progress_percentage}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-mono-data text-[10px] text-[#404040]">
                    {job.actual_hours}/{job.estimated_hours} HRS | {job.assigned_worker || "Unassigned"}
                  </div>
                  <div className="flex gap-1">
                    {job.status === "pending" && (
                      <button
                        data-testid={`start-job-${job.id}`}
                        onClick={() => updateStatus(job.id, "in_progress", 10)}
                        className="px-2 py-1 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] transition-colors"
                      >START</button>
                    )}
                    {job.status === "in_progress" && (
                      <button
                        data-testid={`complete-job-${job.id}`}
                        onClick={() => updateStatus(job.id, "completed", 100)}
                        className="px-2 py-1 text-[10px] font-bold bg-[#34C759] text-white rounded-sm hover:bg-[#2AA04A] transition-colors"
                      >COMPLETE</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateJobDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchJobs} />
    </div>
  );
}
